import React, { useCallback } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { createPopup } from '@typeform/embed';
import '@typeform/embed/build/css/popup.css';
import { Users, BarChart3, Zap, Shield, Globe, Clock, ArrowRight } from 'lucide-react';

const TYPEFORM_ID = 'ZH4dXx8d';

const benefits = [
  {
    icon: Users,
    title: 'AI-Powered Interviews',
    description: 'Duty AI conducts personalized first-round interviews with every candidate, 24/7, in 26+ languages.'
  },
  {
    icon: BarChart3,
    title: 'Data-Driven Hiring',
    description: 'Objective, structured evaluations eliminate bias and surface the best candidates based on real data.'
  },
  {
    icon: Zap,
    title: 'Hire 10x Faster',
    description: 'Screen thousands of candidates simultaneously. Go from application to shortlist in hours, not weeks.'
  },
  {
    icon: Shield,
    title: 'Verified & Fraud-Free',
    description: 'Duty AI validates candidate identity and detects fraud, ensuring the person you interview is the person you hire.'
  },
  {
    icon: Globe,
    title: 'Global Reach',
    description: 'Conduct interviews via video, phone, SMS, or WhatsApp — meeting candidates wherever they are in the world.'
  },
  {
    icon: Clock,
    title: 'Always On',
    description: 'Duty AI works around the clock so candidates can interview at their convenience, increasing completion rates.'
  }
];

const stats = [
  { value: '5,000+', label: 'Interviews daily' },
  { value: '96%', label: 'Candidate satisfaction' },
  { value: '33+', label: 'ATS integrations' },
  { value: '26+', label: 'Languages supported' }
];

const CareersPage = () => {
  const openTypeform = useCallback(() => {
    createPopup(TYPEFORM_ID, {
      opacity: 100,
      hideHeaders: true,
      hideFooter: true,
      size: 80,
    }).open();
  }, []);

  return (
    <>
      <Navbar />
      <main className="bg-[#F9FBF5] min-h-screen">
        {/* Hero */}
        <section className="pt-20 lg:pt-32 pb-16 lg:pb-20">
          <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
            <h1 className="text-[#082900] text-[36px] lg:text-[64px] font-light leading-[1.05] tracking-[-0.02em] mb-6">
              For Employers
            </h1>
            <p className="text-[#082900]/60 text-[16px] lg:text-[20px] font-normal max-w-2xl leading-relaxed mb-10">
              Transform your hiring with Duty AI — the AI recruiter that interviews every candidate, so you only meet the best.
            </p>
            <button
              onClick={openTypeform}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#082900] text-[#EEF2E1] text-[14px] font-normal rounded-full hover:bg-[#0a3500] transition-colors duration-300"
            >
              Get started
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* Stats bar */}
        <section className="border-y border-[#082900]/10">
          <div className="max-w-[1121px] mx-auto px-6 lg:px-10 py-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-[#082900] text-[32px] lg:text-[48px] font-light leading-[1]">{stat.value}</p>
                  <p className="text-[#082900]/50 text-[13px] lg:text-[14px] font-normal mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Duty AI */}
        <section className="py-16 lg:py-24">
          <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
            <h2 className="text-[#082900] text-[28px] lg:text-[48px] font-light leading-[1.1] tracking-[-0.02em] mb-4 text-center">
              Why employers choose Duty AI
            </h2>
            <p className="text-[#082900]/50 text-[14px] lg:text-[16px] font-normal text-center max-w-xl mx-auto mb-14">
              Everything you need to hire faster, smarter, and more fairly.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-2xl p-8 border border-[#082900]/5 hover:border-[#082900]/15 transition-colors duration-300">
                  <b.icon className="w-7 h-7 text-[#082900] mb-5" strokeWidth={1.5} />
                  <h3 className="text-[#082900] text-[17px] lg:text-[18px] font-normal mb-2.5">{b.title}</h3>
                  <p className="text-[#082900]/50 text-[13px] lg:text-[14px] font-normal leading-relaxed">{b.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
            <h2 className="text-[#082900] text-[28px] lg:text-[48px] font-light leading-[1.1] tracking-[-0.02em] mb-14 text-center">
              How it works
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {[
                { step: '01', title: 'Define your role', desc: 'Tell Duty AI what you\'re looking for — role requirements, must-have skills, and evaluation criteria.' },
                { step: '02', title: 'Duty AI interviews everyone', desc: 'Every applicant gets a thorough, personalized interview. No one falls through the cracks.' },
                { step: '03', title: 'Meet the best', desc: 'Review detailed scorecards and interview highlights. Schedule final rounds with your top candidates.' }
              ].map((item, i) => (
                <div key={i} className="text-center lg:text-left">
                  <span className="text-[#082900]/20 text-[48px] lg:text-[64px] font-light leading-[1]">{item.step}</span>
                  <h3 className="text-[#082900] text-[18px] lg:text-[20px] font-normal mt-2 mb-3">{item.title}</h3>
                  <p className="text-[#082900]/50 text-[14px] font-normal leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Get in Touch CTA — Opens Typeform */}
        <section className="py-20 lg:py-32">
          <div className="max-w-[1121px] mx-auto px-6 lg:px-10">
            <div className="bg-[#082900] rounded-3xl p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div>
                <h2 className="text-[#EEF2E1] text-[28px] lg:text-[48px] font-light leading-[1.1] tracking-[-0.02em] mb-3">
                  Get in touch
                </h2>
                <p className="text-[#EEF2E1]/50 text-[14px] lg:text-[16px] font-normal max-w-lg leading-relaxed">
                  Ready to transform your hiring? Fill out a quick form and our team will get back to you within 24 hours.
                </p>
              </div>
              <button
                onClick={openTypeform}
                className="flex-shrink-0 inline-flex items-center gap-2 px-8 py-3.5 border border-[#EEF2E1]/30 text-[#EEF2E1] text-[14px] font-normal rounded-full hover:bg-[#EEF2E1]/10 transition-colors duration-300 cursor-pointer"
              >
                Get in touch
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CareersPage;
